import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {QueryDefinition} from '@reduxjs/toolkit/dist/query';
import {UseLazyQuery} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import capitalize from 'lodash/capitalize';

import {NewRadioField} from '@edna/components';
import {Stack} from '@edna/components/primitives';
import {ECurrencySymbol, TCurrency} from '@edna/utils';

import {SecondaryText} from 'src/components/primitives';
import {DEFAULT_DEBOUNCE_DELAY, EChannelType, TItemId} from 'src/constants';
import {
  ELicensePeriod,
  TActiveTariff,
  TTariffTemplate,
  TWhatsappTariffPayload,
} from 'src/containers/Tariffs/definitions';
import {useDebouncedPromise} from 'src/hooks';
import {axiosBaseQuery} from 'src/models';
import {joi} from 'src/utils/validator';

import {MAX_DECIMAL_SCALE} from './definitions';
import * as S from './style';

type TTariffContentFormData = {
  name: string;
  file?: File | null;
  licensePrice?: number | string;
  licenseCurrency?: TCurrency;

  // whatsapp
  mauCount?: number | string;
  initPayment?: number | string;
  defaultMauPrice?: number | string;
};

const getTariffContentInitialValues = (
  channelType: EChannelType,
  tariff?: TTariffTemplate | TActiveTariff,
) => {
  const isWhatsApp = channelType === EChannelType.WHATSAPP;

  const defaultValues: TTariffContentFormData = {
    name: '',
    licensePrice: tariff?.payload?.licensePrice ?? '',
    licenseCurrency: tariff?.payload?.licenseCurrency ?? tariff?.payload?.currency,
    ...(isWhatsApp
      ? {
          mauCount: '',
          initPayment: '',
          defaultMauPrice: '',
        }
      : {}),
  };

  if (isWhatsApp && tariff?.payload) {
    const payload = tariff.payload as TWhatsappTariffPayload;

    defaultValues.mauCount = payload.mauCount ?? '';
    defaultValues.initPayment = payload.initPayment ?? '';
    defaultValues.defaultMauPrice = payload.defaultMauPrice ?? '';
  }

  return defaultValues;
};

const useTariffNameSchema = (
  i18nErrorKey: string,
  useQuery: UseLazyQuery<
    QueryDefinition<
      {name: string; tenantId?: TItemId},
      ReturnType<typeof axiosBaseQuery>,
      string,
      {exists: boolean}
    >
  >,
) => {
  const [isTariffExists] = useQuery();
  const {id: supportTenantId} = useParams<'id'>();

  const debouncedIsTariffExists = useDebouncedPromise<
    (value: string) => Promise<{exists: boolean}>
  >(
    (value: string) => isTariffExists({name: value, tenantId: supportTenantId}).unwrap(),
    DEFAULT_DEBOUNCE_DELAY,
  );

  const schema = React.useMemo(
    () =>
      joi
        .string()
        .max(1024)
        .required()
        .external(async (value, helpers) => {
          try {
            const {exists} = await debouncedIsTariffExists(value);

            if (exists) {
              return helpers.error(`^${i18nErrorKey}`);
            }

            return value;
          } catch {}
        }),
    [],
  );

  return schema;
};

const SmsViberPersonalTariffContent = React.memo<{
  licenseCurrencyCode?: TCurrency | null;
}>(({licenseCurrencyCode}) => {
  const {t} = useTranslation();

  return (
    <S.Row>
      <S.MinField
        name="licensePrice"
        isDecimal
        decimalScale={MAX_DECIMAL_SCALE}
        label={
          <S.Label>
            {licenseCurrencyCode
              ? t('Tariffs:createPersonalTariff.labels.licensePriceWithCurrency', {
                  currency: ECurrencySymbol[licenseCurrencyCode],
                })
              : t('Tariffs:createPersonalTariff.labels.licensePrice')}
          </S.Label>
        }
      />
    </S.Row>
  );
});

const WhatsAppLicensePeriodSelect = React.memo(() => {
  const {t} = useTranslation();

  return (
    <Stack size="2">
      <SecondaryText size="XS">{t('Tariffs:tariffUpdate.selectLicensePeriod')}</SecondaryText>
      <NewRadioField name="licensePeriodMonths" value={ELicensePeriod.MONTH} noMargin>
        {capitalize(t(`Tariffs:licensePeriod.${ELicensePeriod.MONTH}`))}
      </NewRadioField>
      <NewRadioField name="licensePeriodMonths" value={ELicensePeriod.YEAR} noMargin>
        {capitalize(t(`Tariffs:licensePeriod.${ELicensePeriod.YEAR}`))}
      </NewRadioField>
    </Stack>
  );
});

const WhatsAppPersonalTariffContent = React.memo<{
  readOnly?: boolean;
  currencyCode: TCurrency;
  licenseCurrencyCode: TCurrency;
  dialogCurrencyCode: TCurrency;
  licensePeriodMonths?: ELicensePeriod;
}>(({readOnly, currencyCode, licenseCurrencyCode, licensePeriodMonths}) => {
  const {t} = useTranslation();

  const currency = ECurrencySymbol[currencyCode];
  const licenseCurrency = ECurrencySymbol[licenseCurrencyCode];

  return (
    <S.ModalRow>
      <S.NewNumberInputField
        name="mauCount"
        label={<S.Label>{t('Tariffs:createPersonalTariff.labels.mauCount')}</S.Label>}
        readOnly={readOnly}
      />
      <S.NewNumberInputField
        name="initPayment"
        label={
          <S.Label>
            {t('Tariffs:createPersonalTariff.labels.initPayment', {
              currency,
            })}
          </S.Label>
        }
        isDecimal
        decimalScale={MAX_DECIMAL_SCALE}
        readOnly={readOnly}
      />
      <S.NewNumberInputField
        name="licensePrice"
        label={
          <S.Label>
            {t('Tariffs:createPersonalTariff.labels.licensePriceWithPeriod', {
              currency: licenseCurrency,
              period: t(`Tariffs:licensePeriod.${licensePeriodMonths}`, ''),
            })}
          </S.Label>
        }
        isDecimal
        decimalScale={MAX_DECIMAL_SCALE}
        readOnly={readOnly}
      />
      <S.NewNumberInputField
        name="defaultMauPrice"
        label={
          <S.Label>
            {t('Tariffs:createPersonalTariff.labels.additionalMauCost', {
              currency: licenseCurrency,
            })}
          </S.Label>
        }
        isDecimal
        decimalScale={MAX_DECIMAL_SCALE}
        readOnly={readOnly}
      />
    </S.ModalRow>
  );
});

export {
  WhatsAppPersonalTariffContent,
  WhatsAppLicensePeriodSelect,
  SmsViberPersonalTariffContent,
  useTariffNameSchema,
  getTariffContentInitialValues,
  TTariffContentFormData,
};
